import type { CityConfig } from "../city-template/types";

export const santaRosaConfig: CityConfig = {
  city: "Santa Rosa",
  state: "California",
  stateAbbr: "CA",
  slug: "santa-rosa",
  route: "/cities/santa-rosa",
  title: "Emergency Passport Santa Rosa, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Santa Rosa, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Sonoma County and Northern California Wine Country travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Santa Rosa, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Santa Rosa and Sonoma County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Santa%20Rosa%20California%20rolling%20hills%20with%20vineyard%20rows%20at%20golden%20hour%2C%20Sonoma%20County%20wine%20country%20landscape%2C%20Russian%20River%20valley%20with%20green%20hills%20and%20scattered%20oak%20trees%2C%20warm%20sunset%20light%20with%20amber%20and%20golden%20tones%2C%20pastoral%20Northern%20California%20scenery%2C%20professional%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20beautiful%20wine%20country%20scene&width=1800&height=700&seq=santa-rosa-hero-001&orientation=landscape",
  heroImageAlt: "Santa Rosa California Sonoma County wine country rolling hills with vineyard rows at golden hour",
  geoPosition: "38.4404;-122.7144",
  geoPlacename: "Santa Rosa, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in downtown San Francisco — approximately 55–70 minutes from Santa Rosa via US-101",
  },
  nearbyCitiesZips: [
    { name: "Santa Rosa", zips: "95401, 95403, 95404, 95405, 95407, 95409" },
    { name: "Petaluma", zips: "94952, 94954, 94999" },
    { name: "Rohnert Park", zips: "94928" },
    { name: "Windsor", zips: "95492" },
    { name: "Healdsburg", zips: "95448" },
    { name: "Napa", zips: "94558, 94559, 94581" },
    { name: "Sebastopol", zips: "95472, 95473" },
    { name: "Cloverdale", zips: "95425" },
    { name: "Sonoma", zips: "95476" },
    { name: "Novato", zips: "94945, 94947, 94949" },
    { name: "San Rafael", zips: "94901, 94903" },
    { name: "Ukiah", zips: "95482, 95418" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Sonoma County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "Santa Barbara", route: "/cities/santa-barbara" },
    { name: "Ventura", route: "/cities/ventura" },
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Oakland", route: "/cities/oakland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Santa Rosa remotely via secure courier. We do not have a walk-in office in Santa Rosa — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Sonoma County travelers.",
  fpcOfficeDistance: "Serving all of Sonoma County and Northern California Wine Country",
  serviceAreaIntro: "From the Historic Railroad Square to the Bennett Valley neighborhood, from Montgomery Village to Roseland — Fast Passport Center serves the entire Santa Rosa community. We regularly help travelers from Petaluma, Rohnert Park, Windsor, and Healdsburg get their passports fast.",
  nearbyCitiesIntro: "We serve all Santa Rosa neighborhoods and nearby Wine Country cities including Petaluma, Rohnert Park, Windsor, Healdsburg, Napa, and Sebastopol. If you are in Sonoma County, Napa County, or Marin County, we have you covered.",
  howItWorksIntro: "Most Santa Rosa travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Charles M. Schulz Airport and San Francisco International serving Wine Country travelers, Sonoma County residents can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Santa Rosa and Sonoma County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Santa Rosa? Here are the most common ones we hear from Wine Country travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Wine Country international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Elena V.",
      location: "Santa Rosa, CA",
      rating: 5,
      text: "My wine tour trip to Tuscany was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved our trip.",
      service: "Emergency Passport",
    },
    {
      name: "Patrick D.",
      location: "Petaluma, CA",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Nicole W.",
      location: "Healdsburg, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Bali. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "David K.",
      location: "Windsor, CA",
      rating: 5,
      text: "As a frequent international traveler from Sonoma County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "santa-rosa-1",
      name: "Santa Rosa Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1100 2nd St",
      city: "Santa Rosa",
      state: "CA",
      zip: "95404",
      phone: "(707) 573-4381",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Santa Rosa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-rosa-2",
      name: "Petaluma Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1200 W McKinley St",
      city: "Petaluma",
      state: "CA",
      zip: "94952",
      phone: "(707) 762-4431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18.5 miles from Santa Rosa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-rosa-3",
      name: "Rohnert Park Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6100 State Farm Dr",
      city: "Rohnert Park",
      state: "CA",
      zip: "94928",
      phone: "(707) 585-2911",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7.5 miles from Santa Rosa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for southern Sonoma County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-rosa-4",
      name: "Napa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1531 1st St",
      city: "Napa",
      state: "CA",
      zip: "94559",
      phone: "(707) 226-4431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "22.0 miles from Santa Rosa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Napa County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-rosa-5",
      name: "Windsor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8800 Windsor Rd",
      city: "Windsor",
      state: "CA",
      zip: "95492",
      phone: "(707) 838-4431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.5 miles from Santa Rosa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Sonoma County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-rosa-6",
      name: "Sonoma County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "585 Fiscal Dr",
      city: "Santa Rosa",
      state: "CA",
      zip: "95404",
      phone: "(707) 565-3800",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "1.0 miles from Santa Rosa City Hall",
      website: "https://sonomacounty.ca.gov/CRA/",
      websiteLabel: "Sonoma County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Wine Country photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Sonoma County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Wine Country applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Sonoma County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Wine Country travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Santa Rosa." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Santa Rosa address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Santa Rosa before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Santa Rosa for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most North Bay travelers." },
    { q: "How fast can I renew my passport in Santa Rosa?", a: "Fast Passport Center offers fast passport renewal in Santa Rosa in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my Europe trip?", a: "If your passport expires before your Europe trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Santa Rosa in 24–48 hours so you don't miss your flights." },
    { q: "Can I get a same-day passport appointment near Santa Rosa?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Santa Rosa residents." },
    { q: "Can I expedite a child passport in Santa Rosa?", a: "Yes. Fast Passport Center expedites child passports in Santa Rosa for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Santa Rosa with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Santa Rosa?",
      a: "With Fast Passport Center's emergency courier service, Santa Rosa travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Santa Rosa?",
      a: "Popular options include the Santa Rosa Main Post Office (95404), Petaluma Post Office (94952), Rohnert Park Post Office (94928), and Sonoma County Clerk (95404). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Santa Rosa?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Napa County or Marin County?",
      a: "We serve all of the North Bay including Napa, Yountville, St. Helena, Calistoga, and Marin County communities. Our overnight shipping labels work from any UPS location, so your location within the region doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Santa Rosa?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Santa Rosa?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Santa Rosa and Petaluma offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Santa Rosa?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Santa Rosa?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Sonoma County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Santa Rosa", address: "2080 Santa Rosa Ave, Santa Rosa, CA 95407", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Petaluma", address: "225 S McDowell Blvd, Petaluma, CA 94954", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Santa Rosa", address: "1980 Santa Rosa Ave, Santa Rosa, CA 95407", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Rohnert Park", address: "6765 Commerce Blvd, Rohnert Park, CA 94928", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Santa Rosa", address: "4883 Sonoma Hwy, Santa Rosa, CA 95409", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Healdsburg", address: "435 Healdsburg Ave, Healdsburg, CA 95448", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Windsor", address: "9075 Windsor Rd, Windsor, CA 95492", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Santa Rosa", address: "1900 Santa Rosa Ave, Santa Rosa, CA 95407", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Santa Rosa",
      address: "2000 W College Ave, Santa Rosa, CA 95401",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Petaluma",
      address: "1056 N McDowell Blvd, Petaluma, CA 94954",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Napa",
      address: "2550 Napa Valley Corporate Dr, Napa, CA 94558",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Ukiah",
      address: "351 N Orchard Ave, Ukiah, CA 95482",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Mendocino County and Northern Sonoma residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "STS",
      name: "Charles M. Schulz – Sonoma County Airport",
      distance: "7 miles from Santa Rosa",
      address: "2200 Airport Blvd, Santa Rosa, CA 95403",
      note: "Regional airport serving Sonoma County with domestic flights to major hubs and connections to international destinations.",
      flightUrl: "https://sonomacountyairport.org/",
      flightLabel: "Sonoma County Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "55 miles from Santa Rosa",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via US-101.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "50 miles from Santa Rosa",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Alternative Bay Area airport with domestic flights and international connections. Accessible via I-580.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
  ],
};